import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { api } from '../services/api'
import { useI18n } from '../i18n/useI18n'

/**
 * #1188 Phase 2: Home 残高カードをタップした時に開く 失効日別 内訳 モーダル。
 * charge (購入) 単位で lot を表示、それぞれの失効日 + 残日数を明示。
 * FIFO 消費の厳密計算は別 issue (現状は informational 表示のみ)。
 */
export default function BalanceExpiryModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getExpiryBreakdown>> | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const res = await api.getExpiryBreakdown()
        if (!cancelled) setData(res)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open])

  if (!open) return null

  const activeLots = (data?.lots ?? []).filter((l) => l.days_remaining >= 0)
  activeLots.sort((a, b) => a.expires_at.localeCompare(b.expires_at))

  // #1774 失効した分も見せる。API は「客に『失効した』と見せるため」に期限切れも
  // 返していたのに、ここで捨てていたので**残高が黙って減るだけ**だった。
  // 残額 0 は使い切った分なので出さない (失効ではない)。新しい順に並べる。
  const expiredLots = (data?.lots ?? []).filter((l) => l.days_remaining < 0 && l.amount_jpy > 0)
  expiredLots.sort((a, b) => b.expires_at.localeCompare(a.expires_at))

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-text-primary">
            {t('expiryModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100"
            aria-label={t('expiryModal.close')}
          >
            <X size={20} />
          </button>
        </div>

        <p className="mt-1 font-body text-xs text-text-secondary">
          {t('expiryModal.subtitle')}
        </p>

        {loading && (
          <div className="mt-6 text-center font-body text-sm text-text-secondary">
            {t('expiryModal.loading')}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="font-body text-sm text-red-600">{t('expiryModal.error')}</p>
            <p className="mt-1 break-all font-body text-xs text-red-500">{error}</p>
          </div>
        )}

        {!loading && !error && activeLots.length === 0 && expiredLots.length === 0 && (
          <div className="mt-6 text-center font-body text-sm text-text-secondary">
            {t('expiryModal.empty')}
          </div>
        )}

        {!loading && activeLots.length > 0 && (
          <div className="mt-4 flex max-h-[60vh] flex-col gap-2 overflow-y-auto">
            {activeLots.map((lot) => (
              <div
                key={lot.charge_id}
                className={`flex items-center justify-between rounded-lg border p-3 ${
                  lot.days_remaining <= 30
                    ? 'border-yellow-300 bg-yellow-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="font-display text-base font-semibold text-text-primary">
                      &yen; {lot.amount_jpy.toLocaleString()}
                    </p>
                    {/* #1444 有償/無償の区分表示。**法的要件** — 無償ポイントは
                       前払式支払手段に該当しないが、「利用者から見て明確に区別
                       できること」を満たさないと有償分と一緒に規制対象になる。 */}
                    {!lot.paid && (
                      <span className="rounded-full bg-matcha/15 px-2 py-0.5 font-body text-[10px] font-semibold text-matcha">
                        {t('expiryModal.freeLot')}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 font-body text-xs text-text-secondary">
                    {t('expiryModal.expiresOn', { date: lot.expires_at })}
                  </p>
                </div>
                <div className="text-right">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      lot.days_remaining <= 30
                        ? 'bg-yellow-200 text-yellow-900'
                        : 'bg-gray-200 text-text-secondary'
                    }`}
                  >
                    {t('expiryModal.daysLeft', { days: lot.days_remaining })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && expiredLots.length > 0 && (
          <div className="mt-4">
            <p className="font-body text-xs font-semibold text-text-secondary">
              {t('expiryModal.expiredTitle')}
            </p>
            <div className="mt-2 flex max-h-[30vh] flex-col gap-2 overflow-y-auto">
              {expiredLots.map((lot) => (
                <div
                  key={lot.charge_id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                >
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="font-display text-base font-semibold text-text-tertiary line-through">
                        &yen; {lot.amount_jpy.toLocaleString()}
                      </p>
                      {!lot.paid && (
                        <span className="rounded-full bg-gray-200 px-2 py-0.5 font-body text-[10px] font-semibold text-text-secondary">
                          {t('expiryModal.freeLot')}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 font-body text-xs text-text-tertiary">
                      {t('expiryModal.expiredOn', { date: lot.expires_at })}
                    </p>
                  </div>
                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-text-secondary">
                    {t('expiryModal.expiredBadge')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-5 rounded-lg bg-gray-100 p-3">
          <p className="font-body text-xs leading-5 text-text-secondary">
            {t('expiryModal.note')}
          </p>
        </div>

        <button
          onClick={onClose}
          className="tap-effect mt-5 h-12 w-full rounded-xl bg-navy font-display text-sm font-semibold text-white"
        >
          {t('expiryModal.close')}
        </button>
      </div>
    </div>
  )
}
